import React from "react";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../prim";

/**
 * Anivaze v3 — NATURE / SKY actors (skin-aware).
 *
 * All actors follow the same contract as actors3/index.tsx:
 *   actor(x, y, t, scale?, …opts) → JSX
 * Composed ONLY from Sk* primitives. Animate via <g transform> so seeds
 * stay stable (no hand-drawn boil). Seed range: 1000–1199.
 */

/** A mountain with a triangular body and a snow cap (no animation — static landmark). */
export function skMountain(x: number, y: number, t: number, scale = 1): React.ReactNode {
  void t; // static actor
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Main mountain body */}
      <SkPolygon points={[[-80, 0], [0, -120], [80, 0]]} fill="#78716c" seed={1000} />
      {/* Shadow side */}
      <SkPolygon points={[[10, -90], [0, -120], [80, 0], [30, 0]]} fill="#57534e" seed={1001} />
      {/* Snow cap */}
      <SkPolygon points={[[-22, -80], [0, -120], [22, -80]]} fill="#f1f5f9" seed={1002} />
    </g>
  );
}

/** A rounded leafy bush — three overlapping circles in green. */
export function skBush(x: number, y: number, t: number, scale = 1): React.ReactNode {
  void t; // static actor
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkCircle cx={0}   cy={-14} r={22} fill="#4ade80" seed={1010} />
      <SkCircle cx={-18} cy={-6}  r={18} fill="#22c55e" seed={1011} />
      <SkCircle cx={18}  cy={-6}  r={18} fill="#22c55e" seed={1012} />
      {/* Short stem/base */}
      <SkRect x={-8} y={-4} w={16} h={8} fill="#15803d" seed={1013} />
    </g>
  );
}

/** Two stacked rocks — a classic boulder pair. */
export function skRock(x: number, y: number, t: number, scale = 1): React.ReactNode {
  void t; // static actor
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Larger bottom rock */}
      <SkEllipse cx={0}   cy={-10} rx={30} ry={18} fill="#a8a29e" seed={1020} />
      {/* Smaller top rock */}
      <SkEllipse cx={-6}  cy={-26} rx={18} ry={12} fill="#d6d3d1" seed={1021} />
      {/* Subtle shadow line */}
      <SkLine x1={-28} y1={-2} x2={28} y2={-2} stroke="#78716c" strokeWidth={1} seed={1022} />
    </g>
  );
}

/** A wavy river / stream band — shimmer via horizontal translate from t. */
export function skRiver(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const shimmer = Math.sin(t * 1.8) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Bank / base */}
      <SkEllipse cx={0}   cy={0}   rx={80} ry={14} fill="#7dd3fc" seed={1030} />
      {/* Inner highlight strip — shifts with shimmer */}
      <g transform={`translate(${shimmer} 0)`}>
        <SkEllipse cx={0}   cy={-2}  rx={56} ry={7}  fill="#bae6fd" seed={1031} />
        <SkEllipse cx={20}  cy={2}   rx={24} ry={4}  fill="#e0f2fe" seed={1032} />
      </g>
    </g>
  );
}

/** A small tuft of 5 grass blades that sway gently left–right. */
export function skGrassTuft(x: number, y: number, t: number, scale = 1): React.ReactNode {
  // Each blade sways with a slight phase offset derived from its index (no random).
  const blades = [
    { ox: -14, phase: 0.0, len: 28, color: "#4ade80" },
    { ox: -6,  phase: 0.4, len: 34, color: "#22c55e" },
    { ox:  0,  phase: 0.8, len: 38, color: "#16a34a" },
    { ox:  7,  phase: 1.2, len: 32, color: "#22c55e" },
    { ox: 14,  phase: 1.6, len: 26, color: "#4ade80" },
  ];
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {blades.map((b, i) => {
        const sway = Math.sin(t * 1.4 + b.phase) * 6;
        return (
          <g key={i} transform={`translate(${sway} 0)`}>
            <SkLine
              x1={b.ox}
              y1={0}
              x2={b.ox + sway * 0.5}
              y2={-b.len}
              stroke={b.color}
              strokeWidth={3}
              seed={1040 + i}
            />
          </g>
        );
      })}
    </g>
  );
}

/** A twinkling star — scale and opacity pulse from t. */
export function skStar(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const pulse = 0.7 + 0.3 * Math.sin(t * 3.5);
  const opacity = 0.6 + 0.4 * Math.abs(Math.sin(t * 2.2));
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`scale(${pulse})`} opacity={opacity}>
        {/* 4 diagonal cross spokes */}
        {Array.from({ length: 4 }, (_, i) => {
          const a = (i / 4) * Math.PI;
          return (
            <SkLine
              key={i}
              x1={Math.cos(a) * 10}
              y1={Math.sin(a) * 10}
              x2={-Math.cos(a) * 10}
              y2={-Math.sin(a) * 10}
              stroke="#fde047"
              strokeWidth={2}
              seed={1050 + i}
            />
          );
        })}
        <SkCircle cx={0} cy={0} r={5} fill="#fef08a" seed={1054} />
      </g>
    </g>
  );
}

/** A crescent moon — a full moon with an overlapping disc cut out. */
export function skMoon(x: number, y: number, t: number, scale = 1): React.ReactNode {
  void t; // static actor
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Full moon circle */}
      <SkCircle cx={0}  cy={0}  r={28} fill="#fef9c3" seed={1060} />
      {/* Overlap disc (sky color) — creates the crescent cutout visually */}
      <SkCircle cx={14} cy={-8} r={24} fill="#1e3a5f" seed={1061} />
    </g>
  );
}

/** A 7-arc rainbow using stacked SkEllipse half-rings (clipped by rect). */
export function skRainbow(x: number, y: number, t: number, scale = 1): React.ReactNode {
  void t; // static actor
  const bands: { color: string; rx: number; ry: number; seed: number }[] = [
    { color: "#ef4444", rx: 90, ry: 50, seed: 1070 }, // red   — outermost
    { color: "#f97316", rx: 80, ry: 44, seed: 1071 }, // orange
    { color: "#fbbf24", rx: 70, ry: 38, seed: 1072 }, // yellow
    { color: "#4ade80", rx: 60, ry: 32, seed: 1073 }, // green
    { color: "#38bdf8", rx: 50, ry: 26, seed: 1074 }, // blue
    { color: "#818cf8", rx: 40, ry: 20, seed: 1075 }, // indigo
    { color: "#c084fc", rx: 30, ry: 14, seed: 1076 }, // violet — innermost
  ];
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {/* Clip the bottom half so only the upper arc shows */}
      <defs>
        <clipPath id="rainbow-clip-1000">
          <rect x={-100} y={-100} width={200} height={100} />
        </clipPath>
      </defs>
      <g clipPath="url(#rainbow-clip-1000)">
        {bands.map((b) => (
          <SkEllipse
            key={b.seed}
            cx={0}
            cy={0}
            rx={b.rx}
            ry={b.ry}
            fill={b.color}
            seed={b.seed}
          />
        ))}
      </g>
    </g>
  );
}
